import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { HOME } from "../constants/testIds";
import { useI18n } from "../lib/i18n";
import { BookOpen, Heart, Users, Sparkles, ArrowRight, Calendar as CalendarIcon } from "lucide-react";

const HERO_BG = "https://customer-assets-jai6qajn.emergentagent.net/job_salateen-admin/artifacts/fjqxwggr_screenwriter_temp_1790453175017.jpg";
const DOME = "https://images.unsplash.com/photo-1581141444721-0e6f8fa8397e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtb3NxdWV8ZW58MHx8fHwxNzg1NjEzMjgyfDA&ixlib=rb-4.1.0&q=85";

const PILLARS = [
    { icon: BookOpen, title: "Quran & Tajweed", body: "From basic Iqra to memorization tracks — every child at their own pace, with dedicated instructors." },
    { icon: Heart, title: "Islamic Character", body: "Akhlaq, adab, and prophetic values woven through every classroom, every conversation." },
    { icon: Users, title: "Arabic Language", body: "Read the Quran in its language of revelation with dedicated Arabic tracks for all ages." },
    { icon: Sparkles, title: "Community First", body: "A safe, welcoming home for your children — friendships that last, teachers who care." },
];

export default function Landing() {
    const { t } = useI18n();
    return (
        <main>
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0">
                    <img src={HERO_BG} alt="Mosque at golden hour" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-primary/70" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-32 md:pt-36 md:pb-44 text-primary-foreground">
                    <div className="max-w-3xl">
                        <div className="overline mb-6 opacity-80">Islamic Center of Federal Way</div>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight">
                            Raising hearts that know their Lord —
                            <span className="text-accent italic"> one Sunday</span> at a time.
                        </h1>
                        <p className="mt-6 text-lg opacity-90 max-w-2xl leading-relaxed">
                            Weekend Islamic school for Pre-K through 12th grade. Quran, Arabic, and character —
                            taught with love, structure, and the barakah of community.
                        </p>
                        <div className="mt-4 inline-flex items-baseline gap-3 rounded-full bg-white/10 border border-white/20 px-4 py-2 text-sm">
                            <span className="opacity-75">{t("hero_first_payment")}</span>
                            <span className="font-serif text-lg">$75<span className="text-sm opacity-70">{t("hero_per_child")}</span></span>
                            <span className="opacity-40">·</span>
                            <span className="opacity-75">{t("hero_then_monthly")}</span>
                        </div>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/signup">
                                <Button size="lg" data-testid={HOME.heroRegisterBtn}
                                    className="bg-accent hover:bg-accent/90 text-accent-foreground font-medium h-12 px-8 rounded-full">
                                    Enroll your child <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/programs">
                                <Button size="lg" variant="outline" data-testid={HOME.heroLearnBtn}
                                    className="bg-transparent border-white/40 text-white hover:bg-white/10 h-12 px-8 rounded-full">
                                    Explore programs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* PILLARS — asymmetric */}
            <section data-testid={HOME.aboutSection} className="relative py-24 md:py-32 overflow-hidden">
                <div className="absolute inset-0 pattern-bg opacity-[0.04]" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5 md:sticky md:top-28 self-start">
                        <div className="overline text-brand-stone mb-4">What we teach</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-brand-ink leading-tight">
                            Faith, knowledge, and character — the way it was taught.
                        </h2>
                        <p className="mt-6 text-brand-stone leading-relaxed max-w-md">
                            Every Sunday, our students step into a curriculum that honours tradition and speaks to today.
                            Small classrooms. Big-hearted teachers. Curriculum aligned with Islamic values and academic rigor.
                        </p>
                        <img src={DOME} alt="Islamic architecture" className="mt-10 rounded-2xl w-full aspect-[4/3] object-cover shadow-lg hidden md:block" />
                    </div>
                    <div className="md:col-span-7 grid sm:grid-cols-2 gap-6" data-testid={HOME.programsGrid}>
                        {PILLARS.map((p, i) => (
                            <Card key={p.title}
                                className={`card-lift border-border/70 bg-card ${i % 3 === 0 ? "sm:translate-y-8" : ""}`}>
                                <CardContent className="p-8">
                                    <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary grid place-items-center mb-5">
                                        <p.icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-dash text-xl font-semibold text-brand-ink">{p.title}</h3>
                                    <p className="mt-3 text-sm text-brand-stone leading-relaxed">{p.body}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Grades strip */}
            <section className="relative py-20 bg-primary text-primary-foreground overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="overline opacity-70 mb-3">Grades we serve</div>
                    <h2 className="font-serif text-3xl md:text-4xl">Pre-K through 12th, plus Quran & Arabic tracks.</h2>
                    <div className="mt-10 flex flex-wrap gap-2">
                        {["Pre-K", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Quran", "Arabic"].map(g => (
                            <div key={g} className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm">{g}</div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Community image + values */}
            <section className="py-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div>
                        <div className="overline text-brand-stone">Family-friendly</div>
                        <h2 className="font-serif text-4xl mt-3 text-brand-ink leading-tight">
                            Bring the whole family. <br /><span className="text-accent italic">One account, every child.</span>
                        </h2>
                        <p className="mt-6 text-brand-stone leading-relaxed">
                            Register multiple children under one account and pay one invoice.
                            Secure card payments powered by Square.
                        </p>
                        <ul className="mt-8 space-y-3 text-sm">
                            {["One family account for all your children",
                                "Automatic class placement by grade and age",
                                "Track attendance, grades, and announcements in one place"].map(x => (
                                    <li key={x} className="flex gap-3">
                                        <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2" />
                                        <span className="text-brand-ink/80">{x}</span>
                                    </li>
                                ))}
                        </ul>
                        <Link to="/signup" className="inline-block mt-8">
                            <Button size="lg" className="bg-primary hover:bg-primary/90 rounded-full h-12 px-8" data-testid={HOME.ctaRegister}>
                                Create a family account <ArrowRight className="ml-2 w-4 h-4" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="rounded-3xl bg-primary text-primary-foreground p-10 md:p-16 relative overflow-hidden">
                        <div className="absolute inset-0 pattern-bg opacity-10" />
                        <div className="relative">
                            <div className="overline opacity-70 mb-4">New session</div>
                            <h3 className="font-serif text-3xl md:text-4xl leading-tight max-w-2xl">
                                Registration is open. Your child&apos;s Sunday starts here.
                            </h3>
                            <div className="mt-8 flex flex-wrap gap-4">
                                <Link to="/signup"><Button size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-12 px-8">Enroll now</Button></Link>
                                <Link to="/calendar"><Button size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 rounded-full h-12 px-8 gap-2"><CalendarIcon className="w-4 h-4" />View calendar</Button></Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}
